import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3, Bookmark, Database, EyeOff, FileText, Globe, MessageSquareQuote, Search, Trophy } from "lucide-react";
import { api } from "@/api/client";
import type { LLMMetadataBundle } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Markdown } from "@/components/ui/markdown";
import { ScoreBar } from "@/components/ui/score-bar";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { METHODOLOGY_DISCLOSURE } from "../../../methodology";
import { toCompareResultBrief, toCompareResultBriefFromRich, type CompareResultBrief } from "./compareResultBrief";

type LoadState =
  | { status: "loading" }
  | { status: "loaded"; brief: CompareResultBrief; debug: DebugPayload }
  | { status: "error"; message: string };

interface DebugPayload {
  metadata: LLMMetadataBundle | null;
  analysisStatus: string | null;
  analysisError: string | null;
}

export function CompareResultDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    const comparisonId = Number(id);
    if (!Number.isInteger(comparisonId)) {
      setState({ status: "error", message: "Invalid comparison ID." });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    const load = async () => {
      // Prefer the rich read-model endpoint; only fall back to the legacy
      // endpoint when the *fetch* fails. A bug in the rich-view mapping must
      // surface as an error rather than being silently masked as a fallback.
      let view: Awaited<ReturnType<typeof api.comparisons.getRich>> | null = null;
      try {
        view = await api.comparisons.getRich(comparisonId, { debug: true });
      } catch {
        view = null;
      }

      const brief = view
        ? toCompareResultBriefFromRich(view)
        : toCompareResultBrief(await api.comparisons.get(comparisonId));

      if (cancelled) return;
      setState({
        status: "loaded",
        brief,
        debug: { metadata: brief.metadata, analysisStatus: brief.analysisStatus, analysisError: brief.analysisError },
      });
    };

    load().catch((error: Error) => {
      if (!cancelled) setState({ status: "error", message: error.message || "Unable to load this comparison." });
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.status === "loading") return <Spinner message="Loading research brief..." size="lg" />;

  if (state.status === "error") {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-sm font-semibold text-brand-950">Comparison unavailable</p>
          <p className="mt-1 text-[13px] text-slate-500">{state.message}</p>
          <Button className="mt-5" variant="secondary" onClick={() => navigate("/app/history")}>Back to history</Button>
        </CardContent>
      </Card>
    );
  }

  return <CompareResearchBrief brief={state.brief} debug={state.debug} />;
}

const sectionTitleClass = "flex items-center gap-2 text-sm font-semibold text-brand-950";

function CompareResearchBrief({ brief, debug }: { brief: CompareResultBrief; debug: DebugPayload }) {
  return (
    <article className="animate-fade-up space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/history"><ArrowLeft className="mr-1.5 h-4 w-4" />History</Link>
        </Button>
        <StatusBadge status={brief.status} />
      </div>

      <Card className="border-brand-100 bg-gradient-to-br from-white to-brand-50/40">
        <CardContent className="px-6 py-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge>{brief.isSentiment ? "Sentiment brief" : "Scoring brief"}</Badge>
                {brief.queryMode === "blind_query" && <Badge variant="muted" className="gap-1"><EyeOff className="h-3 w-3" />Blind Query</Badge>}
                <span className="text-[12px] text-slate-600">{new Date(brief.createdAt).toLocaleString()}</span>
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-brand-950">{brief.title}</h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{brief.subtitle}</p>
            </div>
            <div className="rounded-lg border border-white/80 bg-white px-4 py-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-slate-600">Model</p>
              <p className="mt-1 text-sm font-semibold text-brand-950">{brief.modelLabel}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <ExecutiveSummary brief={brief} />
      <Scorecard brief={brief} />
      <Insights brief={brief} />
      <Evidence brief={brief} />
      <Disclosures brief={brief} />
      <RawResponseSection rawResponse={brief.rawResponse} />
      <RawDebug debug={debug} />
    </article>
  );
}

function ExecutiveSummary({ brief }: { brief: CompareResultBrief }) {
  return (
    <Card>
      <CardHeader>
        <h2 className={sectionTitleClass}><Trophy className="h-4 w-4 text-accent-700" />Executive summary</h2>
        <CardDescription>Verdict and top-level interpretation.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-accent-500/20 bg-accent-50/40 px-4 py-3">
          <p className="text-sm font-semibold text-brand-950">{brief.verdict}</p>
        </div>
        {brief.summary ? (
          <Markdown content={brief.summary} className="text-[14px] leading-relaxed text-slate-600" />
        ) : (
          <p className="text-[13px] text-slate-500">No generated summary was returned. Use the scorecard and evidence below for interpretation.</p>
        )}
      </CardContent>
    </Card>
  );
}

function Scorecard({ brief }: { brief: CompareResultBrief }) {
  return (
    <Card>
      <CardHeader>
        <h2 className={sectionTitleClass}><BarChart3 className="h-4 w-4 text-brand-600" />Side-by-side scorecard</h2>
        <CardDescription>{brief.dimensions.length > 0 ? "Scores are shown exactly as returned by the analysis." : "No structured scores were returned."}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 lg:grid-cols-2">
          {brief.brands.map((brand) => (
            <div key={brand.brandId} className={cn("rounded-lg border p-4", brand.isWinner ? "border-accent-500/30 bg-accent-50/20" : "border-slate-200 bg-white", brand.isAbsent && "bg-slate-50")}> 
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-brand-950">{brand.brandName}</h3>
                  {brand.isWinner && <Badge variant="winner">{brief.isSentiment ? "Recommended" : "Winner"}</Badge>}
                  {brand.isAbsent && <Badge variant="muted">Not surfaced</Badge>}
                </div>
                <span className="text-lg font-bold tabular-nums text-brand-950">{brand.averageScore ?? "—"}<span className="ml-0.5 text-xs font-normal text-slate-600">avg</span></span>
              </div>
              {brief.dimensions.length > 0 ? (
                <div className="space-y-2.5">
                  {brief.dimensions.map((dimension) => (
                    <ScoreBar key={dimension} label={dimension} score={brand.scores[dimension] ?? null} isWinner={brand.isWinner && brand.scores[dimension] === brief.maxByDimension[dimension]} />
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-slate-500">No score dimensions available.</p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Insights({ brief }: { brief: CompareResultBrief }) {
  const surfaced = brief.brands.filter((brand) => !brand.isAbsent);
  return (
    <Card>
      <CardHeader>
        <h2 className={sectionTitleClass}><MessageSquareQuote className="h-4 w-4 text-brand-600" />Insights and reasoning</h2>
      </CardHeader>
      <CardContent className="space-y-3">
        {surfaced.length === 0 ? <p className="text-[13px] text-slate-500">No brand-specific reasoning was available.</p> : surfaced.map((brand) => (
          <div key={brand.brandId} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-[13px] font-semibold text-brand-950">{brand.brandName}</p>
            {brand.reasoning && brand.reasoning !== "Not mentioned." ? <Markdown content={brand.reasoning} className="mt-1 text-[13px] text-slate-600" /> : <p className="mt-1 text-[13px] text-slate-500">No reasoning returned for this brand.</p>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Evidence({ brief }: { brief: CompareResultBrief }) {
  return (
    <Card>
      <CardHeader>
        <h2 className={sectionTitleClass}><FileText className="h-4 w-4 text-brand-600" />Evidence and citations</h2>
        <CardDescription>Grounding available from reasoning, quotes, mentions, model response, and provider metadata.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {brief.evidence.length > 0 ? (
          <div className="space-y-3">
            {brief.evidence.map((item) => (
              <blockquote key={item.id} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                <div className="mb-2 flex items-center gap-2"><Badge variant="muted">{item.kind}</Badge><span className="text-[13px] font-semibold text-brand-950">{item.label}</span></div>
                <Markdown content={item.body} className="text-[13px] text-slate-600" />
              </blockquote>
            ))}
          </div>
        ) : <p className="text-[13px] text-slate-500">No evidence text was returned for this comparison.</p>}

        <div>
          <p className="mb-2 text-[13px] font-semibold text-brand-950">Citations</p>
          {brief.citations.length > 0 ? (
            <ol className="list-decimal space-y-1 pl-5 text-[13px] text-slate-600">
              {brief.citations.map((citation) => <li key={citation.id}>{citation.url ? <a className="text-brand-600 hover:underline" href={citation.url} target="_blank" rel="noreferrer">{citation.title}</a> : citation.title} <span className="text-slate-600">({citation.source})</span></li>)}
            </ol>
          ) : <p className="text-[13px] text-slate-500">No provider citations were exposed. This brief still shows available reasoning and response evidence.</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function Disclosures({ brief }: { brief: CompareResultBrief }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card>
        <CardHeader><h2 className={sectionTitleClass}><Search className="h-4 w-4 text-brand-600" />Query fan-out</h2></CardHeader>
        <CardContent className="space-y-2">
          {brief.fanOut.length > 0 ? brief.fanOut.map((call) => (
            <div key={`${call.label}-${call.responseId ?? call.model}`} className="rounded-md bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
              <span className="font-semibold text-brand-950">{call.label}</span>
              <br />
              {call.model} · web search {call.webSearch ? "requested" : "not requested"}
              {call.searchQueries && call.searchQueries.length > 0 && (
                <>
                  <br />
                  <span className="text-slate-500">
                    Searched for: {call.searchQueries.map((q) => `"${q}"`).join(", ")}
                  </span>
                </>
              )}
              {call.webSearch && (!call.searchQueries || call.searchQueries.length === 0) && (
                <>
                  <br />
                  <span className="text-slate-400">Provider did not expose the exact search query text.</span>
                </>
              )}
            </div>
          )) : <p className="text-[13px] text-slate-500">No call metadata returned.</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><h2 className={sectionTitleClass}><Bookmark className="h-4 w-4 text-brand-600" />Cited sources</h2></CardHeader>
        <CardContent>
          {brief.citations.length > 0 ? (
            <ul className="space-y-2 text-[13px] text-slate-600">
              {brief.citations.map((citation) => (
                <li key={citation.id}>
                  {citation.url ? (
                    <a className="text-brand-600 hover:underline" href={citation.url} target="_blank" rel="noreferrer">{citation.title}</a>
                  ) : (
                    <span>{citation.title}</span>
                  )}
                  <span className="text-slate-400"> ({citation.source})</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-slate-500">No citation sources were extracted from annotations. The brief was produced from model reasoning alone, without web-sourced citations.</p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><h2 className={sectionTitleClass}><Globe className="h-4 w-4 text-brand-600" />Web search activity</h2></CardHeader>
        <CardContent className="space-y-2">
          {brief.searchActivity.length > 0 ? brief.searchActivity.map((activity) => (
            <div key={activity.phase} className="rounded-md bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
              <span className="font-semibold text-brand-950">{activity.phase}</span>
              {activity.queries.length > 0 && (
                <>
                  <br />
                  <span className="text-slate-500">
                    Searched for: {activity.queries.map((q) => `"${q}"`).join(", ")}
                  </span>
                </>
              )}
              {activity.queries.length === 0 && (
                <>
                  <br />
                  <span className="text-slate-400">
                    {activity.citedSourceCount > 0
                      ? "Provider returned citations, but did not expose the exact search query text."
                      : "Web search was requested, but the provider did not expose the exact search query text."}
                  </span>
                </>
              )}
              <br />
              <span className="text-slate-400">
                {activity.citedSourceCount > 0
                  ? `Returned ${activity.citedSourceCount} citation source${activity.citedSourceCount !== 1 ? "s" : ""}`
                  : "No annotation citations were returned from this search."}
              </span>
            </div>
          )) : <p className="text-[13px] text-slate-500">No web search was requested during any LLM phase of this comparison.</p>}
        </CardContent>
      </Card>
      <p className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-[12px] leading-relaxed text-slate-500 lg:col-span-3">{METHODOLOGY_DISCLOSURE}</p>
    </div>
  );
}

function RawDebug({ debug }: { debug: DebugPayload }) {
  return (
    <details className="rounded-lg border border-slate-200 bg-white">
      <summary className="flex cursor-pointer items-center gap-2 px-6 py-4 text-sm font-semibold text-brand-950"><Database className="h-4 w-4 text-slate-600" />Raw / debug metadata</summary>
      <div className="border-t border-slate-100 p-6">
        <pre className="max-h-[420px] overflow-auto rounded-md bg-slate-950 p-4 text-[12px] leading-relaxed text-slate-100">{JSON.stringify({ llm_metadata: debug.metadata, analysis_status: debug.analysisStatus, analysis_error: debug.analysisError }, null, 2)}</pre>
      </div>
    </details>
  );
}

function RawResponseSection({ rawResponse }: { rawResponse: string | null }) {
  return (
    <Card>
      <CardHeader>
        <h2 className={sectionTitleClass}><FileText className="h-4 w-4 text-brand-600" />Raw model response</h2>
        <CardDescription>The model’s original response, rendered as markdown.</CardDescription>
      </CardHeader>
      <CardContent>
        {rawResponse ? (
          <Markdown content={rawResponse} className="text-[14px] leading-relaxed text-slate-600" />
        ) : (
          <p className="text-[13px] text-slate-500">No raw response was stored for this comparison.</p>
        )}
      </CardContent>
    </Card>
  );
}
